import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ActivityFiltersProps {
  query: string
  timeRange: string
  accountId: number
}

export default function ActivityFilters({ query: initialQuery, timeRange: initialTimeRange, accountId }: ActivityFiltersProps) {
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [timeRange, setTimeRange] = useState(initialTimeRange)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== initialQuery) {
        router.get(
          `/accounts/${accountId}/activity`,
          { query: searchQuery || undefined, time_range: timeRange },
          { preserveState: true, preserveScroll: true, replace: true }
        )
      }
    }, 300)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, accountId, timeRange])

  // Update URL when time range changes
  useEffect(() => {
    if (timeRange !== initialTimeRange) {
      router.get(
        `/accounts/${accountId}/activity`,
        { query: searchQuery || undefined, time_range: timeRange },
        { preserveState: true, preserveScroll: false, replace: true }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, accountId, searchQuery])

  const handleClearFilters = () => {
    setSearchQuery('')
    setTimeRange('30d')
    router.get(
      `/accounts/${accountId}/activity`,
      {},
      { preserveState: false, preserveScroll: false, replace: true }
    )
  }

  const hasActiveFilters = searchQuery || timeRange !== '30d'

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      {/* Search Input */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder="Search by sender name, email, or subject..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10 bg-gray-100 dark:bg-gray-700 text-blue-950 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 border-gray-300"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Time Range Selector */}
      <div className="sm:w-48">
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24 hours</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="12m">Last 12 months</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="secondary"
          onClick={handleClearFilters}
          className="sm:w-auto"
        >
          <X className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  )
}
